import { refreshConnectorUsages } from "./refreshConnectorUsages";
import type { Transaction } from "@rocicorp/zero";
import { nanoid } from "nanoid";
import {
  copyAllCommitFiles,
  syncFacets,
  updateStepConfig,
} from "./commitFiles";
import { findUniqueName } from "./findUniqueName";
import { zql } from "./generatedDrizzleSchema";
import {
  type ActorAttribution,
  actorColumns,
  type AttributionCtx,
  type CreatorAttribution,
  creatorColumns,
} from "./rbac/roles";
import type { StepConfig } from "./stepConfig";
import { userFacingError } from "./userFacingError";

export interface BranchArgs {
  branchId: string;
  forkBranchId: string;
  forkCommitId: string;
  forkChatId: string;
  workingCommitId: string;
  workingChatId: string;
}

export function makeBranchParams(branchId: string): BranchArgs {
  return {
    branchId,
    forkBranchId: nanoid(12),
    forkCommitId: nanoid(12),
    forkChatId: nanoid(12),
    workingCommitId: nanoid(12),
    workingChatId: nanoid(12),
  };
}

async function forkFromMain(
  tx: Transaction,
  actor: ActorAttribution,
  creator: CreatorAttribution,
  tenantId: string,
  source: {
    headCommitId: string;
    workflowId: string;
  },
  target: { branchId: string; commitId: string; chatId: string }
): Promise<void> {
  const now = Date.now();

  const existingBranches = await tx.run(
    zql.workflowBranches.where("workflowId", source.workflowId)
  );
  const name = findUniqueName(
    "New branch",
    new Set(existingBranches.map((b) => b.name))
  );

  await tx.mutate.workflowBranches.insert({
    id: target.branchId,
    workflowId: source.workflowId,
    userId: actor.userId,
    serviceAccountId: actor.serviceAccountId,
    name,
    headCommitId: target.commitId,
    forkCommitId: source.headCommitId,
    createdAt: now,
    lastChatEntryAt: null,
    tenantId,
  });

  await tx.mutate.workflowCommits.insert({
    id: target.commitId,
    branchId: target.branchId,
    parentCommitId: source.headCommitId,
    chatId: target.chatId,
    message: null,
    createdAt: now,
    createdByUserId: creator.createdByUserId,
    createdByServiceAccountId: creator.createdByServiceAccountId,
    tenantId,
  });

  await copyAllCommitFiles(
    { tx, commitId: source.headCommitId, tenantId },
    target.commitId
  );

  await tx.mutate.chats.insert({
    id: target.chatId,
    commitId: target.commitId,
    createdAt: now,
    tenantId,
  });
}

// A user's per-branch model choice should follow them when a branch forks (e.g.
// chatting on main forks a new branch), so the next message keeps their model.
async function copyBranchModelPreference(
  tx: Transaction,
  userId: string,
  fromBranchId: string,
  toBranchId: string
): Promise<void> {
  const [pref] = await tx.run(
    zql.branchModelPreferences
      .where("userId", userId)
      .where("branchId", fromBranchId)
  );
  if (pref?.aiProviderId != null) {
    await tx.mutate.branchModelPreferences.insert({
      userId,
      branchId: toBranchId,
      aiProviderId: pref.aiProviderId,
      chatModel: pref.chatModel ?? null,
      tenantId: pref.tenantId,
    });
  }
}

export interface ExistingBranch {
  id: string;
  name: string;
  headCommitId: string | null;
  workflowId: string;
}

// Publishing deletes the draft and cascades through its commits, so writers
// take the branch row first to share one lock order and see the current head.
async function lockBranchHead(
  tx: Transaction,
  tenantId: string,
  existing: ExistingBranch
): Promise<string | null> {
  if (tx.location !== "server") return existing.headCommitId;
  const rows = await tx.dbTransaction.query(
    "SELECT head_commit_id FROM workflow_branches WHERE id = $1 AND tenant_id = $2 FOR NO KEY UPDATE",
    [existing.id, tenantId]
  );
  const [row] = [...rows] as Array<{ head_commit_id: string | null }>;
  if (!row) throw userFacingError("Branch not found");
  return row.head_commit_id;
}

export async function ensureWritable(
  tx: Transaction,
  actorCtx: AttributionCtx,
  tenantId: string,
  branch: BranchArgs,
  existing: ExistingBranch
): Promise<{ branchId: string; commitId: string }> {
  const writable = await prepareWritableCommit(
    tx,
    actorCtx,
    tenantId,
    branch,
    existing
  );
  const { userId } = actorColumns(actorCtx);
  if (userId && !actorCtx.automated) {
    await tx.mutate.branchContributors.insert({
      branchId: writable.branchId,
      workflowId: existing.workflowId,
      userId,
      lastMessagedAt: null,
      tenantId,
    });
  }
  return writable;
}

async function prepareWritableCommit(
  tx: Transaction,
  actorCtx: AttributionCtx,
  tenantId: string,
  branch: BranchArgs,
  existing: ExistingBranch
): Promise<{ branchId: string; commitId: string }> {
  const actor = actorColumns(actorCtx);
  const creator = creatorColumns(actorCtx);
  if (existing.id !== branch.branchId) {
    throw new Error(
      `ensureWritable: existing branch ${existing.id} does not match requested branchId ${branch.branchId}`
    );
  }
  const headCommitId = await lockBranchHead(tx, tenantId, existing);
  if (!headCommitId) throw new Error("Branch has no head commit");

  if (existing.name === "main") {
    const target = {
      branchId: branch.forkBranchId,
      commitId: branch.forkCommitId,
      chatId: branch.forkChatId,
    };
    await forkFromMain(
      tx,
      actor,
      creator,
      tenantId,
      { headCommitId, workflowId: existing.workflowId },
      target
    );
    // Model preferences are per-user; a pure service account has none to carry.
    if (actor.userId)
      await copyBranchModelPreference(
        tx,
        actor.userId,
        existing.id,
        target.branchId
      );
    return { branchId: target.branchId, commitId: target.commitId };
  }

  const [headCommit] = await tx.run(
    zql.workflowCommits.where("id", headCommitId)
  );
  if (!headCommit) throw new Error("Head commit not found");

  if (!headCommit.message)
    return { branchId: branch.branchId, commitId: headCommitId };

  const now = Date.now();

  await tx.mutate.workflowCommits.insert({
    id: branch.workingCommitId,
    branchId: branch.branchId,
    parentCommitId: headCommitId,
    chatId: branch.workingChatId,
    message: null,
    createdAt: now,
    createdByUserId: creator.createdByUserId,
    createdByServiceAccountId: creator.createdByServiceAccountId,
    tenantId,
  });

  await copyAllCommitFiles(
    { tx, commitId: headCommitId, tenantId },
    branch.workingCommitId
  );

  await tx.mutate.chats.insert({
    id: branch.workingChatId,
    commitId: branch.workingCommitId,
    createdAt: now,
    tenantId,
  });

  await tx.mutate.workflowBranches.update({
    id: branch.branchId,
    headCommitId: branch.workingCommitId,
  });
  await refreshConnectorUsages(tx, branch.workingCommitId);

  return { branchId: branch.branchId, commitId: branch.workingCommitId };
}

/**
 * Update config.toml fields within a step, then sync facets.
 */
export async function modifyStepConfig(
  tx: Transaction,
  commitId: string,
  tenantId: string,
  stepDir: string,
  updates: Partial<Omit<StepConfig, "id">>
): Promise<void> {
  await updateStepConfig({ tx, commitId, tenantId }, stepDir, updates);
  await syncFacets({ tx, commitId, tenantId }, stepDir);
}
